import { templateToTitle } from '@apps/card/utils/templateToTitle';
import type { CardPdpSeoDataQuery } from './ProductDetailsPage.graphql.generated';
import { useCardPdpSeoDataQuery } from './ProductDetailsPage.graphql.generated';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { Seo } from '@shared/components/Seo';
import React, { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { getNamedCategoryPathFromStationeryTemplateCategory } from '@apps/card/Card.routes';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { getPdpSeoOffer } from './getPdpSeoOffer';
import { isCurrencyCodeType } from '@shared/utils/currency/currency.constants';
import { findMatchingMarketingData } from '@apps/card/routes/Dashboard/components/DesignsGallery/utils';
import { PdpSchema } from './PdpSchema';
import { useGetDiscountByVendor } from '@apps/card/hooks/useGetDiscountByVendor';

type PdpSeoProps = {
  themeId: string;
  currentCategory: StationeryTemplateCategoryEnum;
  /**
   * The template in the URL (a theme has one per size). Resolves the canonical URL and the
   * advertised price to that size; absent on the deprecated `:themeId`-only route, which then
   * canonicalises to the theme's first template.
   */
  templateId?: Maybe<string>;
  /**
   * True on the public shop (`/cards/...`), where the page is a product listing search engines may
   * index. The admin `/:eventHandle/edit` route renders the same head tags but no Product data:
   * that route is disallowed to crawlers and is not the URL a listing should point at.
   */
  isPublicShopRoute?: boolean;
};

type PdpSeoTemplate = CardPdpSeoDataQuery['stationeryTemplatesByThemeId'][number];

/**
 * What the head describes: a printable template, or for a digital-only design (no printable
 * template at all) the ecard design behind it. The two differ only in what they can offer.
 */
type PdpSeoSubject = Readonly<{
  themeId: string;
  name: Maybe<string>;
  thumbnailUrl: Maybe<string>;
  /** The template the canonical URL names; null for a digital-only design, which has none. */
  templateId: string | null;
  products: PdpSeoTemplate['products'];
}>;

type SeoContentProps = {
  subject: PdpSeoSubject;
  currentCategory: StationeryTemplateCategoryEnum;
  isPublicShopRoute: boolean;
};

type CategoryCopy = Partial<Record<StationeryTemplateCategoryEnum, string>>;

/**
 * The one URL search engines should index for a design: the public route with no query string,
 * so `?format=`, `?colorway=` and the admin `/:eventHandle/edit` variants all consolidate here.
 * Printable designs use the V2 route with their templateId (one URL per size); a digital-only
 * design has no template, so it keeps the theme-only route. Public, not host-relative, because
 * the admin route renders this head too.
 */
const getPdpCanonicalUrl = (category: StationeryTemplateCategoryEnum, themeId: string, templateId: string | null) => {
  const base = `https://withjoy.com/cards/wedding/${getNamedCategoryPathFromStationeryTemplateCategory(category)}/product/${encodeURIComponent(themeId)}`;
  return templateId ? `${base}/${encodeURIComponent(templateId)}` : base;
};

// Day-of products are not cards: their sellable product carries its own type, so the advertised
// price has to come from that product, not from a paper card the template does not have.
const CATEGORY_TO_OFFER_PRODUCT_TYPE: CategoryCopy = {
  [StationeryTemplateCategoryEnum.napkin]: 'napkin',
  [StationeryTemplateCategoryEnum.sign]: 'sign',
};

const SeoContent = ({ subject, currentCategory, isPublicShopRoute }: SeoContentProps) => {
  const { tq } = useTranslation('stationery');
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  // The same per-vendor promotion the format picker applies, so the advertised price is the one
  // the page shows. The sale comes from a feature flag the server does not evaluate on this route,
  // so a server render carries the list price and the client's render corrects it; crawlers that
  // run scripts read the corrected block.
  const { getDiscountByVendor } = useGetDiscountByVendor(currentCategory);
  const templateTitle = templateToTitle(subject);

  const TEMPLATE_CATEGORY_TO_TITLE = useMemo<CategoryCopy>(
    () => ({
      [StationeryTemplateCategoryEnum.saveTheDate]: tq`Shop the '${templateTitle}' Save the Date Card | Joy`,
      [StationeryTemplateCategoryEnum.invitation]: tq`Shop the '${templateTitle}' Invitation Card | Joy`,
      [StationeryTemplateCategoryEnum.thankYou]: tq`Shop the '${templateTitle}' Thank You Card | Joy`,
      [StationeryTemplateCategoryEnum.holiday]: tq`Shop the '${templateTitle}' Holiday Card | Joy`,
      [StationeryTemplateCategoryEnum.enclosure]: tq`Shop the '${templateTitle}' Enclosure Card | Joy`,
      [StationeryTemplateCategoryEnum.menu]: tq`Shop the '${templateTitle}' Menu Card | Joy`,
      [StationeryTemplateCategoryEnum.napkin]: tq`Shop the '${templateTitle}' Napkins | Joy`,
      [StationeryTemplateCategoryEnum.sign]: tq`Shop the '${templateTitle}' Wedding Sign | Joy`,
    }),
    [tq, templateTitle],
  );

  // The first sentence names the design and its kind (and makes no format promise: some designs
  // are paper-only, some digital-only); the second carries the price when the sellable product
  // has one. Search engines show this under the title, so it states what the page sells rather
  // than repeating the category page's pitch.
  const TEMPLATE_CATEGORY_TO_DESCRIPTION_LEAD = useMemo<CategoryCopy>(
    () => ({
      [StationeryTemplateCategoryEnum.saveTheDate]: tq`Customize the '${templateTitle}' save the date card from Joy with your names, date and details.`,
      [StationeryTemplateCategoryEnum.invitation]: tq`Customize the '${templateTitle}' wedding invitation from Joy with your names, date and details.`,
      [StationeryTemplateCategoryEnum.thankYou]: tq`Customize the '${templateTitle}' thank you card from Joy with your names, date and details.`,
      [StationeryTemplateCategoryEnum.holiday]: tq`Customize the '${templateTitle}' holiday card from Joy with your names, date and details.`,
      [StationeryTemplateCategoryEnum.enclosure]: tq`Customize the '${templateTitle}' enclosure card from Joy to match your invitation suite.`,
      [StationeryTemplateCategoryEnum.menu]: tq`Customize the '${templateTitle}' menu card from Joy with your menu and details.`,
      [StationeryTemplateCategoryEnum.napkin]: tq`Customize '${templateTitle}' napkins from Joy with your names and date.`,
      [StationeryTemplateCategoryEnum.sign]: tq`Customize the '${templateTitle}' wedding sign from Joy with your names and details.`,
    }),
    [tq, templateTitle],
  );

  // The product's own name for structured data and the feed: the design plus its kind, with no
  // "Shop the" framing and no site suffix, so the listing title Google shows is the product.
  const TEMPLATE_CATEGORY_TO_PRODUCT_NAME = useMemo<CategoryCopy>(
    () => ({
      [StationeryTemplateCategoryEnum.saveTheDate]: tq`${templateTitle} Save the Date Card`,
      [StationeryTemplateCategoryEnum.invitation]: tq`${templateTitle} Wedding Invitation`,
      [StationeryTemplateCategoryEnum.thankYou]: tq`${templateTitle} Thank You Card`,
      [StationeryTemplateCategoryEnum.holiday]: tq`${templateTitle} Holiday Card`,
      [StationeryTemplateCategoryEnum.enclosure]: tq`${templateTitle} Enclosure Card`,
      [StationeryTemplateCategoryEnum.menu]: tq`${templateTitle} Menu Card`,
      [StationeryTemplateCategoryEnum.napkin]: tq`${templateTitle} Napkins`,
      [StationeryTemplateCategoryEnum.sign]: tq`${templateTitle} Wedding Sign`,
    }),
    [tq, templateTitle],
  );

  const title = TEMPLATE_CATEGORY_TO_TITLE[currentCategory] ?? tq`Shop the '${templateTitle}' Card | Joy`;
  const productName = TEMPLATE_CATEGORY_TO_PRODUCT_NAME[currentCategory] ?? tq`${templateTitle} Card`;
  const descriptionLead = TEMPLATE_CATEGORY_TO_DESCRIPTION_LEAD[currentCategory] ?? tq`Customize the '${templateTitle}' card from Joy with your names, date and details.`;

  const offer = getPdpSeoOffer(subject.products, {
    productType: CATEGORY_TO_OFFER_PRODUCT_TYPE[currentCategory],
    getDiscount: product => getDiscountByVendor(product.vendor),
  });
  // The product's own currency, not the visitor's: the price must match the figure the page
  // shows a crawler, which never carries a display-currency preference. A code the formatter
  // does not know drops the sentence rather than printing a number in the wrong currency.
  // Re-spread so the narrowed currency type survives into the variable (a guard narrows only the expression).
  const pricedOffer = offer && isCurrencyCodeType(offer.currency) ? { ...offer, currency: offer.currency } : null;
  const priceSentence = pricedOffer
    ? tq`From ${minorUnitsToPriceString(pricedOffer.unitPriceInMinorUnits, { currency: pricedOffer.currency })} each, with a matching wedding website.`
    : tq`Every design pairs with a matching wedding website.`;

  const canonicalURL = getPdpCanonicalUrl(currentCategory, subject.themeId, subject.templateId);
  const description = `${descriptionLead} ${priceSentence}`;

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonicalURL={canonicalURL}
        url={canonicalURL}
        ogImage={subject.thumbnailUrl || undefined}
        ogType="product"
      />
      {isPublicShopRoute && pricedOffer && subject.templateId && (
        <PdpSchema
          name={productName}
          description={description}
          canonicalURL={canonicalURL}
          imageUrl={subject.thumbnailUrl || undefined}
          sku={subject.templateId}
          // Minor units are hundredths throughout the card app (`useMinorUnitsToPriceString`).
          offer={{
            price: (pricedOffer.unitPriceInMinorUnits / 100).toFixed(2),
            priceCurrency: pricedOffer.currency,
            listPrice: pricedOffer.listUnitPriceInMinorUnits > pricedOffer.unitPriceInMinorUnits ? (pricedOffer.listUnitPriceInMinorUnits / 100).toFixed(2) : undefined,
          }}
        />
      )}
    </>
  );
};

const templateToSubject = (template: PdpSeoTemplate): PdpSeoSubject => ({
  themeId: template.themeId,
  name: template.name,
  thumbnailUrl: template.thumbnailUrl,
  templateId: template.id,
  products: template.products,
});

/** A digital-only design, described from its ecard design's marketing data (as the PDP itself does). */
const ecardDesignToSubject = (ecardDesign: NonNullable<CardPdpSeoDataQuery['ecardDesign']>, category: StationeryTemplateCategoryEnum): PdpSeoSubject | null => {
  const marketingData = findMatchingMarketingData(ecardDesign, category);
  if (!marketingData) return null;
  return { themeId: ecardDesign.themeId, name: ecardDesign.name, thumbnailUrl: marketingData.imageUrl, templateId: null, products: [] };
};

export const PdpSeo = (props: PdpSeoProps) => {
  const { themeId, currentCategory, templateId, isPublicShopRoute = false } = props;
  const { data } = useCardPdpSeoDataQuery({ variables: { themeId, category: currentCategory }, batchMode: 'fast' });

  const templates = data?.stationeryTemplatesByThemeId;
  // Prefer the size the URL names so a 4x6 page does not canonicalise to (or price as) the 5x7.
  const template = (templateId && templates?.find(candidate => candidate.id === templateId)) || templates?.[0];
  const subject = template ? templateToSubject(template) : data?.ecardDesign ? ecardDesignToSubject(data.ecardDesign, currentCategory) : null;

  return (
    <>
      {subject && (
        <SeoContent
          subject={subject}
          currentCategory={currentCategory}
          isPublicShopRoute={isPublicShopRoute}
        />
      )}
    </>
  );
};
